import { Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges } from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { Subscription } from 'rxjs';
import {
  formatFilterDate,
  isEmptyFilterValue,
  toDatepickerValue,
} from 'src/app/pages/operation/shared/operation-filter.utils';

import { CarbonFilter } from '../interfaces/carbon-filter';
import { CharacteristicConsultListService } from '../shared/characteristic-consult-list.service';
import { hasActiveDateRange } from '../shared/characteristic-consult.utils';

@Component({
  standalone: false,
  selector: 'app-characteristic-consult-filter',
  templateUrl: './characteristic-consult-filter.component.html',
  styleUrls: ['./characteristic-consult-filter.component.scss'],
})
export class CharacteristicConsultFilterComponent implements OnInit, OnChanges, OnDestroy {
  @Output() isOpenChange = new EventEmitter<boolean>();
  @Output() closed = new EventEmitter<void>();

  @Input() get isOpen(): boolean {
    return this._isOpen;
  }

  set isOpen(value: boolean) {
    this._isOpen = value;
    this.isOpenChange.emit(this._isOpen);
  }

  formFilter: FormGroup = new FormGroup({});
  datepickerResetKey = 0;
  periodStartDate: Date | null = null;
  periodEndDate: Date | null = null;
  invalidPeriodDate = false;

  private _isOpen = false;
  private initialDateSubscription?: Subscription;
  private finalDateSubscription?: Subscription;

  constructor(
    private readonly formBuilder: FormBuilder,
    private readonly characteristicConsultListService: CharacteristicConsultListService,
  ) {}

  ngOnInit(): void {
    this.setForm();
    this.loadFormFromFilters();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['isOpen']?.currentValue === true) {
      this.loadFormFromFilters();
    }
  }

  ngOnDestroy(): void {
    this.initialDateSubscription?.unsubscribe();
    this.finalDateSubscription?.unsubscribe();
  }

  get minFinalDateIso(): string {
    return this.periodStartDate ? this.toIsoDate(this.periodStartDate) : '';
  }

  get maxInitialDateIso(): string {
    return this.periodEndDate ? this.toIsoDate(this.periodEndDate) : '';
  }

  close(): void {
    this.closed.emit();
  }

  cleanForm(): void {
    this.formFilter.reset({
      financialInstrumentCode: '',
      issuerAccount: '',
      holderAccount: '',
      initialDate: null,
      finalDate: null,
    });
    this.formFilter.get('initialDate')?.setValue(null);
    this.formFilter.get('finalDate')?.setValue(null);
    this.resetPeriodDateValidation();
    this.refreshDatepickerViews();
    this.characteristicConsultListService.setFilters({});
    this.characteristicConsultListService.isSetDate.next(false);
  }

  filter(): void {
    this.syncPeriodDatesFromForm();

    if (this.invalidPeriodDate) {
      return;
    }

    const filters = this.buildFiltersFromForm();
    this.characteristicConsultListService.setFilters(filters);
    this.characteristicConsultListService.isSetDate.next(hasActiveDateRange(filters));
    this.close();
  }

  onInitialDateChange(event: Event): void {
    this.onDateChange(event, 'initialDate');
  }

  onFinalDateChange(event: Event): void {
    this.onDateChange(event, 'finalDate');
  }

  trackDatepickerResetKey(_index: number, key: number): number {
    return key;
  }

  private setForm(): void {
    this.formFilter = this.formBuilder.group({
      financialInstrumentCode: [''],
      issuerAccount: [''],
      holderAccount: [''],
      initialDate: [null],
      finalDate: [null],
    });

    this.initialDateSubscription = this.formFilter.get('initialDate')?.valueChanges.subscribe((value) => {
      this.periodStartDate = isEmptyFilterValue(value) ? null : toDatepickerValue(value);
      this.revalidatePeriodDates();
    });

    this.finalDateSubscription = this.formFilter.get('finalDate')?.valueChanges.subscribe((value) => {
      this.periodEndDate = isEmptyFilterValue(value) ? null : toDatepickerValue(value);
      this.revalidatePeriodDates();
    });
  }

  private loadFormFromFilters(): void {
    const filters = this.characteristicConsultListService.filters.getValue();
    const hasDateFilter = hasActiveDateRange(filters);

    this.formFilter.setValue({
      financialInstrumentCode: filters.carbonCreditCode ?? '',
      issuerAccount: filters.registerCode ?? '',
      holderAccount: filters.holderCode ?? '',
      initialDate: hasDateFilter ? toDatepickerValue(filters.initialDate) : null,
      finalDate: hasDateFilter ? toDatepickerValue(filters.finalDate) : null,
    });

    this.syncPeriodDatesFromForm();
  }

  private onDateChange(event: Event, controlName: 'initialDate' | 'finalDate'): void {
    const iso = (event as CustomEvent<{ value?: string }>).detail?.value?.trim() ?? '';

    if (!iso) {
      this.formFilter.get(controlName)?.setValue(null);
      return;
    }

    const date = toDatepickerValue(iso);

    if (!date) {
      return;
    }

    this.formFilter.get(controlName)?.setValue(date);
  }

  private syncPeriodDatesFromForm(): void {
    const initialDate = this.formFilter.get('initialDate')?.value;
    const finalDate = this.formFilter.get('finalDate')?.value;

    this.periodStartDate = isEmptyFilterValue(initialDate) ? null : toDatepickerValue(initialDate);
    this.periodEndDate = isEmptyFilterValue(finalDate) ? null : toDatepickerValue(finalDate);
    this.revalidatePeriodDates();
  }

  private revalidatePeriodDates(): void {
    this.invalidPeriodDate = !!this.periodStartDate
      && !!this.periodEndDate
      && this.periodStartDate > this.periodEndDate;
  }

  private resetPeriodDateValidation(): void {
    this.periodStartDate = null;
    this.periodEndDate = null;
    this.invalidPeriodDate = false;
  }

  private toIsoDate(date: Date): string {
    return formatFilterDate(date) ?? '';
  }

  private refreshDatepickerViews(): void {
    this.datepickerResetKey++;
  }

  private buildFiltersFromForm(): CarbonFilter {
    const formValues = this.formFilter.getRawValue();
    const initialDate = formatFilterDate(formValues.initialDate);
    const finalDate = formatFilterDate(formValues.finalDate);
    const dateRange = hasActiveDateRange({ initialDate, finalDate });

    return {
      carbonCreditCode: formValues.financialInstrumentCode?.trim() || undefined,
      registerCode: formValues.issuerAccount?.trim() || undefined,
      holderCode: formValues.holderAccount?.trim() || undefined,
      initialDate: dateRange ? initialDate : undefined,
      finalDate: dateRange ? finalDate : undefined,
      filterByInsertDate: dateRange ? true : undefined,
    };
  }
}
